import axios from 'axios'

import appConfig from 'configs/app.config'
import {TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY} from 'constants/api.constant'
import {pushAlert} from 'components/ui'
import store from 'store'
import {onSignOutSuccess, setNewTokens} from 'store/auth/sessionSlice'

const unauthorizedCode = [401]

const BaseService = axios.create({
    baseURL: appConfig.apiPrefix,
    headers: {
        'Access-Control-Allow-Origin': '*',
    },
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
    failedQueue.forEach(({resolve, reject}) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })

    failedQueue = []
}

const getAccessToken = () => store.getState()?.auth?.session?.accessToken
const getRefreshToken = () => store.getState()?.auth?.session?.refreshToken

const isSessionEndpoint = (url = '') => url.includes('/admin-sessions')

const interceptorsRequest = [
    (request) => {
        const accessToken = getAccessToken()

        if (accessToken) {
            request.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
        }

        return request
    },
    (err) => Promise.reject(err),
]

const interceptorsResponse = [
    (response) => response.data,
    async (error) => {
        const {response, config} = error

        if (!config) {
            return Promise.reject(error)
        }

        const statusCode = response?.data?.code || response?.data?.statusCode || response?.status

        const isUnauth = unauthorizedCode.includes(statusCode) || statusCode === 'UNAUTHORIZED_ERROR'

        const refreshToken = getRefreshToken()

        // Для login/logout/refresh вообще не пытаемся запускать refresh flow
        if (isSessionEndpoint(config.url)) {
            return Promise.reject(response?.data || error)
        }

        if (!isUnauth || !refreshToken) {
            return Promise.reject(response?.data || error)
        }

        if (config._retry) {
            return Promise.reject(response?.data || error)
        }

        config._retry = true

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject})
            })
                .then((newAccessToken) => {
                    config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${newAccessToken}`
                    return BaseService(config)
                })
                .catch((err) => Promise.reject(err))
        }

        isRefreshing = true

        try {
            const refreshResponse = await axios.put(`${appConfig.apiPrefix}/admin-sessions`, {refreshToken})

            const {accessToken, refreshToken: newRefreshToken} = refreshResponse.data.data

            store.dispatch(
                setNewTokens({
                    accessToken,
                    refreshToken: newRefreshToken,
                }),
            )

            processQueue(null, accessToken)

            config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`

            return BaseService(config)
        } catch (err) {
            processQueue(err, null)

            pushAlert('danger', 'The current session has expired')
            store.dispatch(onSignOutSuccess())

            return Promise.reject(err)
        } finally {
            isRefreshing = false
        }
    },
]

BaseService.interceptors.request.use(...interceptorsRequest)
BaseService.interceptors.response.use(...interceptorsResponse)

export default BaseService
